/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/react-in-jsx-scope */
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import {
  MenteeObjectiveStats,
  ObjectiveCount,
} from '../../types/MentorDashboardStat';
import { Select } from 'antd';
import { useEffect, useState } from 'react';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ActiveObjectivesChartProps {
  completedObjectives: number | undefined;
  inProgressObjectives: number | undefined;
  notStartedObjectives: number | undefined;
  draftObjectives: number | undefined;
  totalObjectives: number | undefined;
  menteeObjectiveStats: MenteeObjectiveStats[] | undefined;
  objectiveCount: ObjectiveCount[] | undefined;
}
const ActiveObjectivesChart = ({
  completedObjectives,
  inProgressObjectives,
  notStartedObjectives,
  draftObjectives,
  totalObjectives,
  menteeObjectiveStats,
  objectiveCount,
}: ActiveObjectivesChartProps) => {
  const [selectedMentee, setSelectedMentee] = useState<string | null>(null);

  const [filteredObjectives, setFilteredObjectives] = useState({
    completedObjectives,
    inProgressObjectives,
    notStartedObjectives,
    draftObjectives,
    totalObjectives,
  });

  const [customPluginTool, setCustomPluginTool] = useState(totalObjectives);

  useEffect(() => {
    if (
      !selectedMentee ||
      !menteeObjectiveStats ||
      menteeObjectiveStats.length === 0
    ) {
      setFilteredObjectives({
        completedObjectives,
        inProgressObjectives,
        notStartedObjectives,
        draftObjectives,
        totalObjectives,
      });
      setCustomPluginTool(totalObjectives);
    } else {
      const menteeStats = menteeObjectiveStats.find(
        (stat) => stat.menteeName === selectedMentee
      );
      if (menteeStats) {
        setFilteredObjectives({
          completedObjectives: menteeStats.completedObjectives,
          inProgressObjectives: menteeStats.inProgressObjectives,
          notStartedObjectives: menteeStats.notStartedObjectives,
          draftObjectives: menteeStats.draftObjectives,
          totalObjectives: menteeStats.totalObjectives,
        });
        setCustomPluginTool(menteeStats.totalObjectives);
      }
    }
  }, [
    selectedMentee,
    menteeObjectiveStats,
    completedObjectives,
    inProgressObjectives,
    notStartedObjectives,
    draftObjectives,
    totalObjectives,
  ]);

  const data = {
    labels: [
      `Completed Objectives ${filteredObjectives.completedObjectives ? (filteredObjectives.completedObjectives > 0 ? filteredObjectives.completedObjectives.toString().padStart(1, ' ') : ' 0') : 0}`,
      `In Progress Objectives ${filteredObjectives.inProgressObjectives ? (filteredObjectives.inProgressObjectives > 0 ? filteredObjectives.inProgressObjectives.toString().padStart(1, ' ') : ' 0') : 0}`,
      `Not Started Objectives ${filteredObjectives.notStartedObjectives ? (filteredObjectives.notStartedObjectives > 0 ? filteredObjectives.notStartedObjectives.toString().padStart(1, ' ') : ' 0') : 0}`,
      `Draft Objectives ${filteredObjectives.draftObjectives ? (filteredObjectives.draftObjectives > 0 ? filteredObjectives.draftObjectives.toString().padStart(2, ' ') : '  0') : 0}`,
    ],
    datasets: [
      {
        data: [
          filteredObjectives.completedObjectives,
          filteredObjectives.inProgressObjectives,
          filteredObjectives.notStartedObjectives,
          filteredObjectives.draftObjectives,
        ],
        backgroundColor: [
          'rgba(98, 178, 253, 1)',
          'rgba(155, 223, 196, 1)',
          'rgba(156, 39, 176, 1)',
          'rgba(253, 156, 19, 1)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(155, 223, 196, 1)',
          'rgba(156, 39, 176, 1)',
          'rgba(253, 156, 19, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const options: any = {
    cutout: '68%',
    plugins: {
      legend: {
        display: filteredObjectives.totalObjectives
          ? filteredObjectives.totalObjectives > 0
          : 0,
        position: 'right',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          pointRadius: 0.4,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        callbacks: {
          title: function () {
            return '';
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: function (context: any) {
            // if All is selected
            if (
              !selectedMentee &&
              objectiveCount &&
              objectiveCount.length > 0
            ) {
              return objectiveCount.map(
                (count) => `${count.menteeName}: (${count.objectiveCount})`
              );
            }
            return context.formattedValue;
          },
          labelTextColor: function () {
            return 'rgba(97, 97, 97, 1)';
          },
          afterBody: (
            context: {
              chart: { tooltip: { options: { backgroundColor: string } } };
            }[]
          ) => {
            const backgroundColor = 'white';
            context[0].chart.tooltip.options.backgroundColor = backgroundColor;
          },
        },
      },
      datalabels: {
        display: false,
      },
      customPluginOptions: {
        totalObjectives: customPluginTool,
      },
    },
    layout: {
      padding: 5,
    },
  };

  const plugins: any = [
    {
      id: 'centerText',
      beforeDraw: function (chart: {
        ctx: any;
        chartArea: { right: number; left: number; top: any; bottom: any };
        config: {
          options: {
            plugins: { customPluginOptions: { totalObjectives: any } };
          };
        };
      }) {
        const ctx = chart.ctx;

        ctx.restore();

        const innerRadius =
          (chart.chartArea.right - chart.chartArea.left) * 0.32;

        const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

        const total =
          chart?.config?.options?.plugins?.customPluginOptions?.totalObjectives;

        if (total && total >= 0) {
          const totalFontSize = innerRadius / 3;
          const numberFontSize = innerRadius / 4;

          ctx.font = `bold ${totalFontSize}px sans-serif`;
          ctx.textBaseline = 'bottom';
          ctx.textAlign = 'center';
          ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';

          const totalText = 'Objectives';

          ctx.fillText(totalText, centerX, centerY);

          ctx.font = `${numberFontSize}px sans-serif`;
          ctx.textBaseline = 'top';

          ctx.fillText(`${total}`, centerX, centerY + totalFontSize / 2);
        }

        ctx.save();
      },
    },
  ];

  // mentees drop down
  const menteeOptions = menteeObjectiveStats
    ? ['All', ...menteeObjectiveStats.map((stat) => stat.menteeName)]
    : [];

  const DropdownComponent = ({
    menteeOptions,
    selectedMentee,
    setSelectedMentee,
  }: {
    menteeOptions: string[];
    selectedMentee: string | null;
    setSelectedMentee: React.Dispatch<React.SetStateAction<string | null>>;
  }) => {
    return (
      <div className="mb-4 w-full max-w-[250px] -mt-9">
        <Select
          value={selectedMentee || 'All'}
          onChange={(value) =>
            setSelectedMentee(value === 'All' ? null : value)
          }
          style={{ width: '100%' }}
          placeholder="Select Mentee"
        >
          {menteeOptions.map((menteeName) => (
            <Select.Option key={menteeName} value={menteeName}>
              {menteeName}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  return (
    // mentees drop down
    <div>
      <div className="flex flex-col items-end">
        <DropdownComponent
          menteeOptions={menteeOptions}
          selectedMentee={selectedMentee}
          setSelectedMentee={setSelectedMentee}
        />
      </div>

      <div className="flex justify-center items-center w-full h-full">
        <div className="w-[500px] h-[400px] relative">
          <Doughnut
            data={data}
            options={options}
            plugins={plugins}
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
};

export default ActiveObjectivesChart;
